import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import DatePicker from "@/components/ui/datepicker";
import MapComponent from "@/components/ui/map";
import { Checkbox } from "@/components/ui/checkbox";
import Chip from "@/components/ui/chip";
import Tooltip from "@/components/ui/tooltip";
import { Close } from "@/components/icons";
import ConfirmModal from "@/components/ui/confirm-modal";
import type {
  PlanningGroupFromApi,
  PersonnelRequestEntry,
  PlanningScheduledTime,
} from "../types/request-and-approval.types";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";
import { educationCatalogApi } from "@/features/education-catalog/services/education-catalog.api";
import type { UpdateVirtualClassroomDto } from "@/features/education-catalog/types/virtual-classroom.types";
import type {
  Education,
  PresetLocationApi,
} from "@/features/education-management/types/education-management.types";
import { EDUCATION_TYPE_API } from "@/features/education-management/types/education-management.types";
import AttendanceModal from "@/features/education-management/components/AttendanceModal";
import { normalizeClassroomDateRange } from "../utils/normalizeClassroomDateRange";
import { weekdaysInRange } from "@/features/client-educations/utils/scheduled-times";
import { sanitizeJitsiRoomName } from "@/utils";
import VirtualClassroomFilesPanel from "./VirtualClassroomFilesPanel";
import styles from "./EditVirtualClassroomModal.module.scss";
import {
  buildClassroomMailUrl,
  collectClassroomMailRecipients,
} from "../utils/classroomMailRecipients";

type DayOption = { label: string; value: string };

export interface EditVirtualClassroomModalProps {
  isOpen: boolean;
  onClose: () => void;
  virtualClassroomId: string;
  /** Modal'ı açan eğitimin ID'si — eğitmen diff hesabı için kullanılır
   *  (sınıfa atanan eğitmen eğitimde yoksa otomatik eğitime de eklenir). */
  educationId?: string;
  group: PlanningGroupFromApi | null;
  educationType: number | null;
  presetLocations: PresetLocationApi[];
  presetLocationOptions: { label: string; value: string }[];
  dayOptions: DayOption[];
  /** @deprecated always true — kept for API compat */
  canViewLocations?: boolean;
  /** Henüz hiçbir sınıfa atanmamış bekleyen personel — bu sınıfa eklenebilir */
  addablePersonnel?: PersonnelRequestEntry[];
  onSaved: () => void;
}

const PARTICIPANTS_POST_MAX = 20;

function chunkUserIds(ids: string[], size: number): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    out.push(ids.slice(i, i + size));
  }
  return out;
}

function parseIsoToDate(iso?: string): Date | null {
  if (!iso?.trim()) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const EditVirtualClassroomModal: React.FC<EditVirtualClassroomModalProps> = ({
  isOpen,
  onClose,
  virtualClassroomId,
  educationId,
  group,
  educationType,
  presetLocations,
  presetLocationOptions,
  dayOptions,
  canViewLocations: _canViewLocations,
  addablePersonnel = [],
  onSaved,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [jitsiRoomName, setJitsiRoomName] = useState("");
  const [jitsiRoomNameError, setJitsiRoomNameError] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  // Kullanıcı DatePicker'ı elle değiştirdiyse true. handleSave'de yalnız bu
  // bayrak true ise startDate/endDate body'ye eklenir — aksi halde "Sınıfı
  // Tamamla" ile geçmişe çekilen endDate, normalize sırasında günün sonuna geri
  // ittirilip sınıf yanlışlıkla "tamamlanmamış" durumuna dönüyordu.
  const [dateRangeDirty, setDateRangeDirty] = useState(false);
  const [presetLocationId, setPresetLocationId] = useState("");
  const [location, setLocation] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [scheduledTimes, setScheduledTimes] = useState<
    Array<PlanningScheduledTime & { id: string }>
  >([]);
  const [removedUserIds, setRemovedUserIds] = useState<Set<string>>(new Set());
  const [addedUserIds, setAddedUserIds] = useState<Set<string>>(new Set());
  const [selectedInstructorIds, setSelectedInstructorIds] = useState<string[]>([]);
  const [instructorOptions, setInstructorOptions] = useState<
    { label: string; value: string }[]
  >([]);
  /** Eğitime şu an atanmış eğitmen ID'leri — sınıfa atanan eğitmenden eksik olan
   *  varsa kayıt sırasında önce eğitime de eklemek için diff hesabı yapılır. */
  const [educationInstructorIds, setEducationInstructorIds] = useState<Set<string>>(
    new Set(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [includeSupervisorsInMail, setIncludeSupervisorsInMail] = useState(false);
  const [includeManagersInMail, setIncludeManagersInMail] = useState(false);

  const attendanceEducation = useMemo<Education | null>(() => {
    if (!educationId) return null;
    return {
      id: educationId,
      educationCode: "",
      educationName: group?.name ?? "",
      educatorName: "",
      format: "canli",
      targetAudience: 0,
      registrationCount: 0,
      publishDate: "",
      status: "aktif",
      createdAt: "",
      updatedAt: "",
    } satisfies Education;
  }, [educationId, group?.name]);

  const resetFromGroup = useCallback(() => {
    if (!group) return;
    setName(group.name ?? "");
    setJitsiRoomName(group.jitsiRoomName ?? "");
    setJitsiRoomNameError("");
    setDateRange([
      parseIsoToDate(group.startDate),
      parseIsoToDate(group.endDate),
    ]);
    setDateRangeDirty(false);
    setPresetLocationId(group.presetLocationId?.trim() ?? "");
    setLocation(group.location ?? "");
    setLatitude(
      group.latitude != null ? String(group.latitude) : "",
    );
    setLongitude(
      group.longitude != null ? String(group.longitude) : "",
    );
    setScheduledTimes(
      (group.scheduledTimes ?? []).map((st, i) => ({
        id: `st-${i}-${st.day}-${st.startTime}`,
        day: st.day,
        startTime: st.startTime,
        endTime: st.endTime,
        timezone: st.timezone ?? "Europe/Istanbul",
      })),
    );
    setRemovedUserIds(new Set());
    setAddedUserIds(new Set());
    const initialInstructorIds =
      group.instructorIds ?? (group.instructors ?? []).map((i) => i.userID);
    setSelectedInstructorIds(initialInstructorIds);
  }, [group]);

  useEffect(() => {
    if (isOpen && group) {
      resetFromGroup();
    }
  }, [isOpen, group, resetFromGroup]);

  useEffect(() => {
    if (!isOpen || !educationId) {
      return;
    }
    let cancelled = false;

    // Tüm domain eğitmenleri — dropdown seçenekleri.
    void educationCatalogApi
      .getInstructors()
      .then((items) => {
        if (cancelled) return;
        setInstructorOptions(
          items.map((i) => ({
            value: i.id,
            label:
              [i.firstName, i.lastName].filter(Boolean).join(" ").trim() ||
              i.id,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setInstructorOptions([]);
      });

    // Eğitime atanmış eğitmenler — diff hesabı için.
    void educationCatalogApi
      .listEducationInstructors(educationId)
      .then((items) => {
        if (cancelled) return;
        setEducationInstructorIds(new Set(items.map((i) => i.userID)));
      })
      .catch(() => {
        if (!cancelled) setEducationInstructorIds(new Set());
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, educationId]);

  const updateScheduleRow = (
    timeId: string,
    patch: Partial<PlanningScheduledTime>,
  ) => {
    setScheduledTimes((prev) =>
      prev.map((row) => (row.id === timeId ? { ...row, ...patch } : row)),
    );
  };

  const addScheduleRow = () => {
    setScheduledTimes((prev) => [
      ...prev,
      {
        id: `time-${Date.now()}`,
        day: 1,
        startTime: "",
        endTime: "",
        timezone: "Europe/Istanbul",
      },
    ]);
  };

  const removeScheduleRow = (timeId: string) => {
    setScheduledTimes((prev) => prev.filter((r) => r.id !== timeId));
  };

  const toggleRemoveUser = (userId: string) => {
    setRemovedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAddUser = (userId: string) => {
    setAddedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const enrollUserIdsInClass = useMemo(() => {
    return new Set((group?.enrollments ?? []).map((e) => e.userId));
  }, [group?.enrollments]);

  const addableCandidates = useMemo(() => {
    return addablePersonnel.filter(
      (p) => p.userId && !enrollUserIdsInClass.has(p.userId),
    );
  }, [addablePersonnel, enrollUserIdsInClass]);

  const handleSave = async () => {
    if (!group) return;

    if (educationType === EDUCATION_TYPE_API.ONLINE && !jitsiRoomName.trim()) {
      const message = t("educationManager.editVirtualClassJitsiRequired");
      setJitsiRoomNameError(message);
      toast.error(message);
      return;
    }

    const isOnlineEducation = educationType === EDUCATION_TYPE_API.ONLINE;
    const hasPreset = presetLocationId.trim().length > 0;
    if (!isOnlineEducation && !hasPreset) {
      toast.error(
        t("educationManager.groupLocationRequired"),
      );
      return;
    }

    const [start, end] = dateRange;
    if (start && end && start > end) {
      toast.error(
        t("educationManager.dateRangeInvalid"),
      );
      return;
    }

    const removed = Array.from(removedUserIds);
    if (removed.length > 20) {
      toast.error(
        t("educationManager.removedUsersMax"),
      );
      return;
    }

    const toAdd = Array.from(addedUserIds);

    const scheduledPayload = scheduledTimes
      .filter((row) => row.startTime && row.endTime)
      .map((row) => ({
        day: Number(row.day),
        startTime: row.startTime,
        endTime: row.endTime,
        ...(row.timezone ? { timezone: row.timezone } : {}),
      }));

    // Haftalık günler kursiyer ekranında tarih aralığına yayılarak somut oturumlara
    // çevrilir. Seçilen günlerden biri aralığa hiç denk gelmiyorsa (ör. tek günlük
    // Salı aralığına Cumartesi saati) o gün için hiç oturum üretilmez ve kursiyer
    // "oturum planlanmadı" görür. Bunu kaydetmeden önce engelle.
    if (scheduledPayload.length > 0 && start && end) {
      const present = weekdaysInRange(start, end);
      const missing = scheduledPayload.some((s) => !present.has(s.day));
      if (missing) {
        toast.error(t("educationManager.scheduleDayOutOfRange"));
        return;
      }
    }

    const initialInstructorIds = (
      group.instructorIds ?? (group.instructors ?? []).map((i) => i.userID)
    )
      .slice()
      .sort();
    const sortedSelected = selectedInstructorIds.slice().sort();
    const instructorsChanged =
      initialInstructorIds.length !== sortedSelected.length ||
      initialInstructorIds.some((id, idx) => id !== sortedSelected[idx]);

    const body: UpdateVirtualClassroomDto = {
      name: name.trim() || undefined,
      jitsiRoomName: isOnlineEducation
        ? jitsiRoomName.trim() || undefined
        : undefined,
      presetLocationId:
        !isOnlineEducation && hasPreset
          ? presetLocationId.trim()
          : undefined,
      location:
        isOnlineEducation || hasPreset ? undefined : location.trim() || undefined,
      latitude:
        isOnlineEducation || hasPreset
          ? undefined
          : Number(latitude) || undefined,
      longitude: isOnlineEducation || hasPreset
        ? undefined
        : Number(longitude) || undefined,
      // Aynı gün başlayıp biten sınıflarda backend'in "start >= end" uyarısına
      // takılmamak için gün başı (00:00) ve gün sonu (23:59) yapıyoruz.
      // ÖNEMLİ: Tarih body'ye yalnız kullanıcı DatePicker'ı elle değiştirdiyse
      // eklenir. Aksi halde "Sınıfı Tamamla" ile geçmişe çekilen endDate,
      // normalize sırasında günün sonuna (23:59) tekrar itilip sınıfı yanlışlıkla
      // "tamamlanmamış" durumuna döndürüyordu.
      ...(dateRangeDirty
        ? (() => {
            const { startDateIso, endDateIso } = normalizeClassroomDateRange(
              start,
              end,
            );
            return {
              startDate: startDateIso,
              endDate: endDateIso,
            };
          })()
        : {}),
      scheduledTimes:
        scheduledPayload.length > 0 ? scheduledPayload : undefined,
      removedUserIds: removed.length > 0 ? removed : undefined,
      instructorIds: instructorsChanged ? selectedInstructorIds : undefined,
    };

    const hasPatchPayload =
      body.name != null ||
      body.jitsiRoomName != null ||
      body.presetLocationId != null ||
      body.location != null ||
      body.latitude != null ||
      body.longitude != null ||
      body.startDate != null ||
      body.endDate != null ||
      (body.scheduledTimes != null && body.scheduledTimes.length > 0) ||
      (body.removedUserIds != null && body.removedUserIds.length > 0) ||
      body.instructorIds !== undefined;

    if (!hasPatchPayload && toAdd.length === 0) {
      toast.error(
        t("educationManager.editVirtualClassNothingToUpdate"),
      );
      return;
    }

    setIsSaving(true);
    try {
      // Sınıfa atanan eğitmenler eğitime de atanmış olmak zorunda (BE 400 döner).
      // Eksik olanları önce eğitim eğitmen listesine ekle, sonra sınıf PATCH'i.
      if (
        instructorsChanged &&
        educationId &&
        selectedInstructorIds.length > 0
      ) {
        const toAddToEducation = selectedInstructorIds.filter(
          (id) => !educationInstructorIds.has(id),
        );
        if (toAddToEducation.length > 0) {
          await educationCatalogApi.addEducationInstructors(
            educationId,
            toAddToEducation,
          );
        }
      }
      for (const chunk of chunkUserIds(toAdd, PARTICIPANTS_POST_MAX)) {
        await virtualClassroomsApi.addParticipants(virtualClassroomId, chunk);
      }
      if (hasPatchPayload) {
        await virtualClassroomsApi.update(virtualClassroomId, body);
      }
      toast.success(
        t("educationManager.editVirtualClassSuccess"),
      );
      onSaved();
      onClose();
    } catch {
      // apiClient toast
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await virtualClassroomsApi.remove(virtualClassroomId);
      toast.success(
        t("educationManager.deleteVirtualClassSuccess"),
      );
      setShowDeleteConfirm(false);
      onSaved();
      onClose();
    } catch {
      // apiClient toast
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCompleteClass = async () => {
    if (!virtualClassroomId) return;
    setIsCompleting(true);
    try {
      // Karar diyaloğu yok: sınıfı doğrudan tamamlandı işaretle.
      // Yoklama "Yoklama Al" butonu üzerinden ayrı olarak alınır.
      await virtualClassroomsApi.complete(virtualClassroomId);
      toast.success(t("educationManager.completeVirtualClassSuccess"));
      setShowCompleteConfirm(false);
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message;
      if (msg) toast.error(msg);
      // apiClient default toast covers the rest
    } finally {
      setIsCompleting(false);
    }
  };

  const handleSendMail = async () => {
    const recipientIds = (group?.enrollments ?? [])
      .map((en) => en.userId)
      .filter((id): id is string => Boolean(id));
    if (recipientIds.length === 0) {
      toast.error(t("educationManager.sendMailNoRecipients"));
      return;
    }
    const allRecipientIds = await collectClassroomMailRecipients({
      virtualClassroomId: virtualClassroomId ?? undefined,
      traineeIds: recipientIds,
      includeSupervisors: includeSupervisorsInMail,
      includeManagers: includeManagersInMail,
    });
    navigate(buildClassroomMailUrl(allRecipientIds, virtualClassroomId ?? undefined), {
      replace: false,
    });
  };

  const isPresetSelected = presetLocationId.trim().length > 0;
  const enrollments = group?.enrollments ?? [];

  const isCompleted = useMemo(() => {
    const iso = group?.endDate;
    if (!iso) return false;
    const end = new Date(iso);
    if (Number.isNaN(end.getTime())) return false;
    return end.getTime() <= Date.now();
  }, [group?.endDate]);

  const completeTooltipContent = isCompleted
    ? t("educationManager.completeVirtualClassTooltipDone")
    : t("educationManager.completeVirtualClassTooltip");

  const completionBadge = (
    <Chip
      type={isCompleted ? "success" : "warning"}
      variant="soft"
      size="small"
    >
      {isCompleted
        ? t("educationManager.completeVirtualClassBadgeDone")
        : t("educationManager.completeVirtualClassBadgePending")}
    </Chip>
  );

  if (!isOpen || !group) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isSaving && onClose()}
      title={t("educationManager.editVirtualClassTitle")}
      size="xl"
      loading={isSaving}
      customFooter={
        <div className={styles.modalFooter}>
          <div className={styles.modalFooterRow}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSaving || isDeleting || isCompleting}
            >
              {t("educationManager.deleteVirtualClass")}
            </Button>
            {!isCompleted ? (
              <Tooltip content={completeTooltipContent} position="top">
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  type="button"
                  onClick={() => setShowCompleteConfirm(true)}
                  disabled={isSaving || isDeleting || isCompleting}
                  loading={isCompleting}
                >
                  {t("educationManager.completeVirtualClass")}
                </Button>
              </Tooltip>
            ) : null}
            {educationId ? (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                type="button"
                onClick={() => setShowAttendance(true)}
                disabled={isSaving || isDeleting || isCompleting}
              >
                {t("educationManager.takeAttendance")}
              </Button>
            ) : null}
            <Button
              variant="outlined"
              color="primary"
              size="small"
              type="button"
              onClick={() => void handleSendMail()}
              disabled={isSaving || isDeleting || isCompleting}
            >
              {t("educationManager.sendMailToClass")}
            </Button>
            <Checkbox
              label={t("educationManager.sendMailIncludeSupervisors")}
              value={includeSupervisorsInMail}
              onChange={() => setIncludeSupervisorsInMail((v) => !v)}
            />
            <Checkbox
              label={t("educationManager.sendMailIncludeManagers")}
              value={includeManagersInMail}
              onChange={() => setIncludeManagersInMail((v) => !v)}
            />
          </div>
          <div
            className={`${styles.modalFooterRow} ${styles.modalFooterRowMain}`}
          >
            <Button
              variant="text"
              color="secondary"
              type="button"
              onClick={() => !isSaving && onClose()}
              disabled={isSaving || isDeleting || isCompleting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={() => void handleSave()}
              disabled={isSaving || isDeleting || isCompleting}
              loading={isSaving}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      }
    >
      <div className={styles.form}>
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>
            {t("educationManager.completeVirtualClassStatusLabel")}
          </span>
          {completionBadge}
        </div>
        <Input
          label={t("educationManager.groupName")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {educationType === EDUCATION_TYPE_API.ONLINE && (
          <Input
            // Online eğitimde zorunlu; boşken Kaydet sessizce geri dönüyordu ve
            // kullanıcı kaydın neden olmadığını anlamıyordu.
            required
            label={t("educationManager.jitsiRoomName")}
            placeholder="EduStream-..."
            value={jitsiRoomName}
            error={!!jitsiRoomNameError}
            errorMessage={jitsiRoomNameError}
            onChange={(e) => {
              setJitsiRoomName(sanitizeJitsiRoomName(e.target.value));
              if (jitsiRoomNameError) setJitsiRoomNameError("");
            }}
          />
        )}
        <DatePicker
          label={t("educationManager.groupDate")}
          isRange
          value={dateRange}
          onChange={(value) => {
            setDateRange(Array.isArray(value) ? value : [value, value]);
            setDateRangeDirty(true);
          }}
        />

        <Dropdown
          label={t("educationManager.virtualClassroomInstructorsLabel")}
          placeholder={t(
            "educationManager.virtualClassroomInstructorsPlaceholder",
          )}
          options={instructorOptions}
          value={selectedInstructorIds}
          multiple
          searchable
          onSelect={(value) => {
            const next = Array.isArray(value)
              ? value
              : value
                ? [value]
                : [];
            setSelectedInstructorIds(next);
          }}
          selectedLabel={instructorOptions
            .filter((o) => selectedInstructorIds.includes(o.value))
            .map((o) => o.label)
            .join(", ")}
        />

        {educationType !== EDUCATION_TYPE_API.ONLINE && (
          <Dropdown
            label={t("educationManager.groupPresetLocation")}
            placeholder={t("educationManager.groupPresetLocationPlaceholder")}
            options={presetLocationOptions}
            value={presetLocationId}
            selectedLabel={
              presetLocationOptions.find((o) => o.value === presetLocationId)
                ?.label ?? ""
            }
            onSelect={(value) => {
              const id = Array.isArray(value) ? (value[0] ?? "") : value;
              const selected = presetLocations.find((l) => l.id === id);
              if (selected) {
                setPresetLocationId(selected.id);
                setLocation(selected.address ?? "");
                setLatitude(
                  selected.latitude != null ? String(selected.latitude) : "",
                );
                setLongitude(
                  selected.longitude != null ? String(selected.longitude) : "",
                );
              }
            }}
            searchable
          />
        )}

        {educationType !== EDUCATION_TYPE_API.ONLINE &&
          isPresetSelected && location && (
          <div className={styles.readOnlyField}>
            <span className={styles.readOnlyLabel}>
              {t("educationManager.groupAddress")}
            </span>
            <span className={styles.readOnlyValue}>{location}</span>
          </div>
        )}

        {educationType !== EDUCATION_TYPE_API.ONLINE &&
          isPresetSelected &&
          latitude &&
          longitude && (
          <div className={styles.mapWrap}>
            <label className={styles.mapLabel}>
              {t("educationManager.groupMap")}
            </label>
            <MapComponent
              center={{
                lat: Number(latitude) || 41.0082,
                lng: Number(longitude) || 28.9784,
              }}
              zoom={14}
              height="200px"
              selectedLocation={{
                lat: Number(latitude),
                lng: Number(longitude),
                address: location || "",
              }}
            />
          </div>
        )}

        <div className={styles.scheduleSection}>
          <div className={styles.scheduleHeader}>
            <h4>{t("educationManager.schedule")}</h4>
            <Button variant="outlined" size="small" type="button" onClick={addScheduleRow}>
              {t("educationManager.addSchedule")}
            </Button>
          </div>
          {scheduledTimes.length === 0 ? (
            <p className={styles.scheduleEmpty}>
              {t("educationManager.scheduleEmpty")}
            </p>
          ) : (
            scheduledTimes.map((row) => (
              <div key={row.id} className={styles.scheduleRow}>
                <Dropdown
                  label={t("educationManager.day")}
                  options={dayOptions}
                  value={String(row.day)}
                  selectedLabel={
                    dayOptions.find((o) => o.value === String(row.day))?.label
                  }
                  onSelect={(value) =>
                    updateScheduleRow(row.id, { day: Number(value) })
                  }
                />
                <Input
                  type="time"
                  label={t("educationManager.startTime")}
                  value={row.startTime}
                  onChange={(e) =>
                    updateScheduleRow(row.id, { startTime: e.target.value })
                  }
                />
                <Input
                  type="time"
                  label={t("educationManager.endTime")}
                  value={row.endTime}
                  onChange={(e) =>
                    updateScheduleRow(row.id, { endTime: e.target.value })
                  }
                />
                <button
                  type="button"
                  className={styles.scheduleRemove}
                  onClick={() => removeScheduleRow(row.id)}
                  aria-label={t("common.remove")}
                >
                  <Close width={16} height={16} />
                </button>
              </div>
            ))
          )}
        </div>

        {virtualClassroomId && (
          <VirtualClassroomFilesPanel classroomId={virtualClassroomId} />
        )}

        {addableCandidates.length > 0 && (
          <div className={styles.addSection}>
            <h4>
              {t("educationManager.addToClassHint")}
            </h4>
            <ul className={styles.addList}>
              {addableCandidates.map((p) => (
                <li key={p.userId} className={styles.addRow}>
                  <span className={styles.addName}>
                    {p.personelAdi}
                    {p.unvan ? (
                      <span className={styles.addTitle}> — {p.unvan}</span>
                    ) : null}
                  </span>
                  <Checkbox
                    label={t("educationManager.addToClass")}
                    value={addedUserIds.has(p.userId)}
                    onChange={() => toggleAddUser(p.userId)}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        {enrollments.length > 0 && (
          <div className={styles.removeSection}>
            <h4>
              {t("educationManager.removeFromClassHint")}
            </h4>
            <ul className={styles.removeList}>
              {enrollments.map((en) => {
                const fullName = [en.firstName, en.lastName]
                  .filter(Boolean)
                  .join(" ")
                  .trim();
                return (
                  <li key={en.enrollmentId} className={styles.removeRow}>
                    <span className={styles.removeName}>
                      {fullName || en.userId}
                      {en.title ? (
                        <span className={styles.removeTitle}> — {en.title}</span>
                      ) : null}
                    </span>
                    <Checkbox
                      label={t("educationManager.removeFromClass")}
                      value={removedUserIds.has(en.userId)}
                      onChange={() => toggleRemoveUser(en.userId)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t("educationManager.deleteVirtualClassTitle")}
        message={t("educationManager.deleteVirtualClassMessage")}
        confirmText={t("educationManager.deleteVirtualClass")}
        cancelText={t("common.cancel")}
        loading={isDeleting}
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={showCompleteConfirm}
        onClose={() => !isCompleting && setShowCompleteConfirm(false)}
        onConfirm={handleCompleteClass}
        title={t("educationManager.completeVirtualClassTitle")}
        message={t("educationManager.completeVirtualClassMessage")}
        confirmText={t("educationManager.completeVirtualClass")}
        cancelText={t("common.cancel")}
        loading={isCompleting}
      />

      <AttendanceModal
        isOpen={showAttendance}
        onClose={() => {
          setShowAttendance(false);
          // Yoklama kayıt sonrası request-and-approval listesi yenilensin.
          onSaved();
          // Sınıf zaten tamamlanmışken yeni yoklama girilmişse, state machine'in
          // ilerleyebilmesi için backend'in `complete()` derive akışını yeniden
          // tetikle. Idempotent: ENROLLED dışı enrollment'lar atlanır, endDate
          // geçmişse korunur, push bildirim aynı dedupe key ile gider.
          if (isCompleted && virtualClassroomId) {
            void virtualClassroomsApi.complete(virtualClassroomId).then(() => {
              onSaved();
            });
          }
        }}
        education={attendanceEducation}
        lockedVirtualClassroomId={virtualClassroomId}
        lockedClassroomName={group?.name ?? undefined}
      />
    </Modal>
  );
};

export default EditVirtualClassroomModal;
